import {useContext, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel} from '@mui/material';
//context
import {AuctionDetailsContext} from '../AuctionDetailsContext';
//helpers
import {getErrorMessage} from '@/utils/helpers';
//services
import {OrganizationAuctionService} from '@/utils/services';
//types
import {IOrganizationAuctionBidData, IOrganizationAuctionBidResponse} from '@/utils/types';
//constants
import {
  BID,
  BID_NUMBER_CAMEL,
  DOLLAR,
  EDIT_ITEM,
  FIRST_NAME,
  FIRST_NAME_CAMEL,
  HASH,
  ID,
  LAST_NAME,
  LAST_NAME_CAMEL,
  RIGHT,
  SEARCH,
  SEAT_TYPE,
  SEAT_TYPE_CAMEL,
  TABLE,
  TABLE_NAME,
  TABLE_NAME_CAMEL,
  defaultPage,
} from '@/utils/constants';
//components
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Loader from '@/shared/components/Loader';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
//styles
import {auctionDetailsBidsStyle} from './AuctionDetailsBids.styles';
import Modal from '@/shared/components/Modal';
import {auctionTableStyle} from '../../AuctionTable.styles';
import AuctionItem from '../../AuctionItem';

const AuctionDetailsBids = () => {
  const {editableAuction, onCloseModal} = useContext(AuctionDetailsContext);

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [searchName, setSearchName] = useState('');
  const [sortColumn, setSortColumn] = useState<string>();
  const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

  const [bidsData, setBidsData] = useState<IOrganizationAuctionBidResponse>();

  const [isShowAuctionModal, setIsShowAuctionModal] = useState<boolean>(false);

  const handlePressEnter = () => {
    setCurrentPage(defaultPage);
    if (editableAuction) getAuctionBids(searchName);
  };

  useEffect(() => {
    if (editableAuction) getAuctionBids();
  }, [editableAuction, currentPage, sortColumn, isOrderDesc]);

  const handleSearchChange = (value: string) => {
    setSearchName(value);
    getAuctionBids(value);
  };

  const handleSortChange = (newSortColumn: string) => () => {
    const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
    setIsOrderDesc(isAsc);
    setSortColumn(newSortColumn);
  };

  const getAuctionBids = async (value?: string) => {
    setIsDataLoaded(false);

    try {
      const reqBody = {
        page: currentPage,
        sortColumn,
        isOrderDesc,
        searchName: value,
        itemId: editableAuction!.id,
      };

      const bidsResponse = await OrganizationAuctionService.getAuctionBids(reqBody);
      setBidsData(bidsResponse.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDataLoaded(true);
    }
  };

  return (
    <Box sx={auctionDetailsBidsStyle.wrapper}>
      <Box sx={auctionDetailsBidsStyle.content}>
        <SearchHeader
          page={currentPage}
          items={bidsData?.totalCount || 0}
          total={bidsData?.totalCount}
          searchPlaceholder={SEARCH}
          onPressEnter={handlePressEnter}
          onSearchChange={handleSearchChange}
        />

        {!!bidsData?.totalCount && isDataLoaded ? (
          <Box sx={auctionDetailsBidsStyle.tableContent}>
            <TableContainer sx={auctionDetailsBidsStyle.container}>
              <Table sx={auctionDetailsBidsStyle.table}>
                <TableHead>
                  <TableRow sx={auctionDetailsBidsStyle.tableHeader}>
                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(BID_NUMBER_CAMEL)} IconComponent={UpDownIcon}>
                        {BID}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(FIRST_NAME_CAMEL)} IconComponent={UpDownIcon}>
                        {FIRST_NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(LAST_NAME_CAMEL)} IconComponent={UpDownIcon}>
                        {LAST_NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(ID)} IconComponent={UpDownIcon}>
                        {TABLE}
                        {HASH}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(TABLE_NAME_CAMEL)} IconComponent={UpDownIcon}>
                        {TABLE_NAME}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel active onClick={handleSortChange(SEAT_TYPE_CAMEL)} IconComponent={UpDownIcon}>
                        {SEAT_TYPE}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align={RIGHT}>
                      <TableSortLabel active onClick={handleSortChange(BID)} IconComponent={UpDownIcon}>
                        {BID}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {bidsData?.data.map((row: IOrganizationAuctionBidData) => (
                    <TableRow key={row.id} sx={auctionDetailsBidsStyle.tableBody}>
                      <TableCell>
                        {HASH}
                        {row.id}
                      </TableCell>
                      <TableCell>{row.firstName}</TableCell>
                      <TableCell>{row.lastName}</TableCell>
                      <TableCell>{row.tableNumber}</TableCell>
                      <TableCell>{row.tableName}</TableCell>
                      <TableCell>{row.seatType}</TableCell>
                      <TableCell align={RIGHT}>
                        {DOLLAR}
                        {row.bid}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={auctionDetailsBidsStyle.pagination}>
              <Pagination
                page={currentPage}
                pageSize={bidsData?.pageSize}
                total={bidsData?.totalCount}
                onChange={(step) => setCurrentPage(step)}
              />
            </Box>
          </Box>
        ) : (
          !bidsData?.totalCount && isDataLoaded && <EmptyTableData />
        )}

        {!isDataLoaded && <Loader parentClassName={auctionDetailsBidsStyle.loader} size={50} />}
      </Box>

      {/* <TableFooterActions
        titleButtonPrev={CANCEL}
        titleButtonNext={EDIT}
        onClickButtonNext={() => setIsShowAuctionModal(true)}
        onClickButtonPrev={onCloseModal}
      /> */}
      {/* <Box sx={auctionDetailsBidsStyle.footerSpacer} /> */}

      <Modal
        onClose={() => setIsShowAuctionModal(false)}
        open={isShowAuctionModal}
        sx={auctionTableStyle.auctionModal}
        title={EDIT_ITEM}
      >
        <AuctionItem onCloseModal={() => setIsShowAuctionModal(false)} editableAuction={editableAuction} />
      </Modal>
    </Box>
  );
};

export default AuctionDetailsBids;
